import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppSettings } from '../../utils/app.settings';
import {
  IStandardPredictionPostRequest,
  IStandardPredictionPostResponse,
  IStandardPredictionRequest,
} from '../../interfaces/standard-prediction.interface';
import { Observable } from 'rxjs';
import { IGetStandardPredictionResponse } from '../../interfaces/standard-prediction.interface';
import {
  IGetFiltersResponse,
  IGetFiltersRequest,
  IGetFiltersSemanaSantaRequest,
  IGetEstacionalRequest,
} from '../../interfaces/estacional-prediction.interface';

type PredictionType = 'holyweek' | 'recessweek' | 'endyear' | 'vacation';

@Injectable({
  providedIn: 'root',
})
export class EstacionalPredictionService {
  constructor(
    private appSetting: AppSettings,
    private httpClient: HttpClient
  ) {}

  private formatDate(date: string | Date): string {
    if (!date) return '';

    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  getSeasonalPredictionFilters(): Observable<IGetFiltersResponse[]> {
    const url = this.appSetting.estacionalPrediction.urls.base;
    return this.httpClient.get<IGetFiltersResponse[]>(url);
  }

  getSeasonalPredictionEdit(
    payload: IGetFiltersRequest,
    predictionType: PredictionType
  ): Observable<IStandardPredictionPostResponse> {
    const url = `${this.appSetting.estacionalPrediction.urls.base}/${predictionType}/edit`;
    return this.httpClient.post<IStandardPredictionPostResponse>(url, payload);
  }

  getSeasonalPredictionEditSemanaSanta(
    payload: IGetFiltersSemanaSantaRequest
  ): Observable<IStandardPredictionPostResponse> {
    const url = `${this.appSetting.estacionalPrediction.urls.base}/holyweek/edit`;
    return this.httpClient.post<IStandardPredictionPostResponse>(url, payload);
  }

  getSeasonalPredictionTemporal(
    uuid: string
  ): Observable<IGetStandardPredictionResponse[]> {
    const url = this.appSetting.estacionalPrediction.urls.temporal;
    return this.httpClient.get<IGetStandardPredictionResponse[]>(url, {
      params: {
        uuid: uuid,
      },
    });
  }

  postGenerateSeasonalPrediction(
    request: IStandardPredictionPostRequest,
    predictionType: PredictionType
  ): Observable<IStandardPredictionPostResponse> {
    const url = `${this.appSetting.estacionalPrediction.urls.generate}${predictionType}/generate`;
    return this.httpClient.post<IStandardPredictionPostResponse>(url, request);
  }

  postGenerateSeasonalSemanaSanta(
    payload: IStandardPredictionPostRequest
  ): Observable<IStandardPredictionPostResponse> {
    const url = `${this.appSetting.estacionalPrediction.urls.base}/holyweek/generate`;
    return this.httpClient.post<IStandardPredictionPostResponse>(url, payload);
  }

  getEstacionalPrediction(
    request: IGetEstacionalRequest
  ): Observable<IGetStandardPredictionResponse[]> {
    let params = new HttpParams();

    Object.entries(request).forEach(([key, value]) => {
      if (key === 'baseCalculo') {
        return;
      }

      if (key === 'fechaInicio' || key === 'fechaFin') {
        const dateValue = value instanceof Date ? value.toISOString() : value;
        params = params.set(key, this.formatDate(dateValue));
      } else if (key === 'filters' && value) {
        if (typeof value === 'string') {
          const [filterKey, filterValue] = value.split('=');
          if (filterKey && filterValue) {
            params = params.set(filterKey, filterValue);
          }
        } else {
          // ✅ Crear una copia mutable del objeto filters para evitar errores de inmutabilidad
          const mutableValue = { ...value };
          if ('idMedidor' in mutableValue) {
            mutableValue['cliente'] = mutableValue['idMedidor'];
            delete mutableValue['idMedidor'];
          }
          Object.entries(mutableValue).forEach(([filterKey, filterValue]) => {
            if (filterValue !== null && filterValue !== undefined) {
              if (Array.isArray(filterValue)) {
                filterValue.forEach(item => {
                  if (item !== null && item !== undefined && item !== '') {
                    params = params.append(filterKey, String(item));
                  }
                });
              } else {
                params = params.set(filterKey, String(filterValue));
              }
            }
          });
        }
      } else if (value !== null && value !== undefined) {
        params = params.set(key, value);
      }
    });
    const url = this.appSetting.estacionalPrediction.urls.getEstacional;
    return this.httpClient.get<IGetStandardPredictionResponse[]>(url, {
      params,
    });
  }
}
